import React, { useMemo, useState } from 'react';
import { Award, Search, Users } from 'lucide-react';

const STATUS_BADGE = {
  approved: { bg: '#e6f4ea', color: '#1e7e34', label: 'Approved' },
  pending: { bg: '#fff8e1', color: '#a66a00', label: 'Pending' },
  rejected: { bg: '#fdeaea', color: '#c62828', label: 'Rejected' },
  revision: { bg: '#eef1fb', color: '#3949ab', label: 'Revision' },
  need_revision: { bg: '#eef1fb', color: '#3949ab', label: 'Revision' },
  revision_requested: { bg: '#eef1fb', color: '#3949ab', label: 'Revision' },
};

const getField = (item, field, fallback = '') => String(item?.[field] ?? fallback).trim();

const StatusBadge = ({ status }) => {
  const key = String(status || '').toLowerCase();
  const meta = STATUS_BADGE[key] || { bg: '#eef0f2', color: '#495057', label: status || '—' };
  return (
    <span
      className="badge"
      style={{ background: meta.bg, color: meta.color, fontWeight: 500 }}
    >
      {meta.label}
    </span>
  );
};

const CompetencyList = ({ items = [], isLoading = false, error = '' }) => {
  const [mode, setMode] = useState('competency');
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');
  const [qualificationFilter, setQualificationFilter] = useState('');
  const [standardFilter, setStandardFilter] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const qualificationOptions = useMemo(
    () => Array.from(new Set(items.map((i) => getField(i, 'qualificationName')).filter(Boolean))).sort(),
    [items]
  );
  const standardOptions = useMemo(
    () => Array.from(new Set(items.map((i) => getField(i, 'standard')).filter(Boolean))).sort(),
    [items]
  );
  const sectorOptions = useMemo(
    () => Array.from(new Set(items.map((i) => getField(i, 'sectors')).filter(Boolean))).sort(),
    [items]
  );
  const statusOptions = useMemo(
    () => Array.from(new Set(items.map((i) => getField(i, 'status')).filter(Boolean))).sort(),
    [items]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (qualificationFilter && getField(i, 'qualificationName') !== qualificationFilter) return false;
      if (standardFilter && getField(i, 'standard') !== standardFilter) return false;
      if (sectorFilter && getField(i, 'sectors') !== sectorFilter) return false;
      if (statusFilter && getField(i, 'status') !== statusFilter) return false;
      if (q) {
        const haystack = [
          i.employeeName,
          i.employeeId,
          i.qualificationName,
          i.standard,
          i.sectors,
          i.status,
        ]
          .map((v) => String(v || '').toLowerCase())
          .join(' ');
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, qualificationFilter, standardFilter, sectorFilter, statusFilter]);

  const byCompetency = useMemo(() => {
    const map = new Map();
    filtered.forEach((i) => {
      const key = getField(i, 'qualificationName', '(No qualification)') || '(No qualification)';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(i);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const byPerson = useMemo(() => {
    const map = new Map();
    filtered.forEach((i) => {
      const key = getField(i, 'employeeName') || getField(i, 'employeeId') || `User #${i.user_id ?? '?'}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(i);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const groups = mode === 'competency' ? byCompetency : byPerson;
  const visibleGroups = showAll ? groups : groups.slice(0, 4);
  const hiddenGroupCount = Math.max(groups.length - visibleGroups.length, 0);

  return (
    <div className="card mt-4 hris-roster-card">
      <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div>
          <h3 className="card-title mb-0">Competency Roster</h3>
          <div className="text-muted small mt-1">
            Read-only roster, visible for all logged-in users. Showing {filtered.length} records.
          </div>
        </div>
        <div className="btn-group" role="group" aria-label="View mode">
          <button
            type="button"
            className={`btn btn-sm ${mode === 'competency' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setMode('competency')}
          >
            <Award size={14} className="me-1" /> Per Kompetensi
          </button>
          <button
            type="button"
            className={`btn btn-sm ${mode === 'person' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setMode('person')}
          >
            <Users size={14} className="me-1" /> Per Orang
          </button>
        </div>
      </div>

      <div className="card-body border-bottom">
        <div className="row g-2">
          <div className="col-12 col-xl-4">
            <div className="input-icon">
              <span className="input-icon-addon"><Search size={16} /></span>
              <input
                type="text"
                className="form-control"
                placeholder="Search name, employee id, qualification, standard..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="col-6 col-md-3 col-xl-2">
            <select
              className="form-select"
              value={qualificationFilter}
              onChange={(e) => setQualificationFilter(e.target.value)}
            >
              <option value="">All Qualifications</option>
              {qualificationOptions.map((q) => <option key={q} value={q}>{q}</option>)}
            </select>
          </div>
          <div className="col-6 col-md-3 col-xl-2">
            <select
              className="form-select"
              value={standardFilter}
              onChange={(e) => setStandardFilter(e.target.value)}
            >
              <option value="">All Standards</option>
              {standardOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="col-6 col-md-3 col-xl-2">
            <select
              className="form-select"
              value={sectorFilter}
              onChange={(e) => setSectorFilter(e.target.value)}
            >
              <option value="">All Sectors</option>
              {sectorOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="col-6 col-md-3 col-xl-2">
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="card-body">
        {isLoading && <div className="text-muted">Loading...</div>}
        {!isLoading && error && <div className="text-danger">{error}</div>}
        {!isLoading && !error && groups.length === 0 && (
          <div className="text-muted">No competency records found.</div>
        )}

        {!isLoading && !error && visibleGroups.map(([groupLabel, rows]) => (
          <div key={groupLabel} className="mb-4">
            <div className="d-flex align-items-center gap-2 mb-2">
              <h4 className="mb-0">{groupLabel}</h4>
              <span className="badge bg-secondary-lt">{rows.length}</span>
            </div>
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    {mode === 'competency' ? (
                      <>
                        <th>Name</th>
                        <th>Employee ID</th>
                        <th>Standard</th>
                        <th>Sector</th>
                        <th>Status</th>
                      </>
                    ) : (
                      <>
                        <th>Qualification</th>
                        <th>Standard</th>
                        <th>Sector</th>
                        <th>Status</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      {mode === 'competency' ? (
                        <>
                          <td>{r.employeeName || '—'}</td>
                          <td>{r.employeeId || '—'}</td>
                          <td>{r.standard || '—'}</td>
                          <td>{r.sectors || '—'}</td>
                          <td><StatusBadge status={r.status} /></td>
                        </>
                      ) : (
                        <>
                          <td>{r.qualificationName || '—'}</td>
                          <td>{r.standard || '—'}</td>
                          <td>{r.sectors || '—'}</td>
                          <td><StatusBadge status={r.status} /></td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {!isLoading && !error && hiddenGroupCount > 0 && (
          <div className="hris-roster-more">
            <button type="button" className="btn btn-outline-primary" onClick={() => setShowAll(true)}>
              View all roster groups ({hiddenGroupCount} more)
            </button>
          </div>
        )}
        {!isLoading && !error && showAll && groups.length > 4 && (
          <div className="hris-roster-more">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setShowAll(false)}>
              Show compact roster
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetencyList;
